import React from 'react';
import { FlatList, View, Text, StyleSheet } from 'react-native';
import { useTenantContext } from '../../contexts';
import UserItem from './UserItem';
import EmptyTenant from './components/EmptyTenant';
import { IUser } from '../../types/user';
import { IInvitation } from '../../types/invitation';
import UserSkeleton from '../../components/skeletons/UserSkeleton';

/**
 * UsersTable component
 *
 * @return {*}
 */
const UsersTable: React.FC = () => {
  const tenantContext = useTenantContext();

  const walletUsers = tenantContext.users || [];
  const pendingInvitations = tenantContext.invitations || [];
  const hasUsers = walletUsers.length > 0;
  const hasInvitations = pendingInvitations.length > 0;
  const hasAnyData = hasUsers || hasInvitations;

  const renderUserItem = ({ item, index }: { item: IUser; index: number }) => (
    <UserItem user={item as IUser & IInvitation} key={index} />
  );

  const renderInvitationItem = ({ item, index }: { item: IInvitation; index: number }) => (
    <UserItem user={item as IUser & IInvitation} key={index} />
  );

  const renderSectionHeader = (title: string, count: number) => (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionTitle}>{title}</Text>
      <Text style={styles.sectionCount}>({count})</Text>
    </View>
  );

  const ListFooterComponent = () => (tenantContext.isRefreshing ? <UserSkeleton /> : null);

  if (tenantContext.isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <UserSkeleton />
        <UserSkeleton />
        <UserSkeleton />
      </View>
    );
  }

  if (!hasAnyData) {
    return <EmptyTenant />;
  }

  return (
    <View style={styles.container}>
      {hasUsers && (
        <View style={styles.section}>
          {renderSectionHeader('Active Wallets', walletUsers.length)}
          <FlatList
            data={walletUsers}
            renderItem={renderUserItem}
            keyExtractor={(item) => item.email}
            scrollEnabled={false}
            nestedScrollEnabled={true}
          />
        </View>
      )}

      {hasInvitations && (
        <View style={styles.section}>
          {renderSectionHeader('Pending Invitations', pendingInvitations.length)}
          <FlatList
            data={pendingInvitations}
            renderItem={renderInvitationItem}
            keyExtractor={(item) => item.email}
            scrollEnabled={false}
            nestedScrollEnabled={true}
            ListFooterComponent={ListFooterComponent}
            ListFooterComponentStyle={styles.listFooter}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1
  },
  loadingContainer: {
    flex: 1,
    gap: 10
  },
  section: {
    marginBottom: 20
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef'
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#495057',
    marginRight: 8
  },
  sectionCount: {
    fontSize: 14,
    fontWeight: '400',
    color: '#6c757d'
  },
  listFooter: {
    marginBottom: 20
  }
});

export default UsersTable;
